'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import AuthBrandPanel from './AuthBrandPanel';
import AppLogo from '@/components/ui/AppLogo';
import OrganizerLogos from '@/components/ui/OrganizerLogos';
import EventPhotoGallery from './EventPhotoGallery';
import OrganisingTeamSection from './OrganisingTeamSection';
import InterestRegistrationForm from './InterestRegistrationForm';
import { Lock, Hash, Loader2, AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

export type AuthRole = 'student' | 'admin';
export type AuthTab = 'login' | 'signup';

interface CodeLoginFormData {
  attendanceCode: string;
}

function UniqueCodeLogin() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [codeLoginError, setCodeLoginError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CodeLoginFormData>();

  const onCodeSubmit = async (data: CodeLoginFormData) => {
    setIsLoading(true);
    setCodeLoginError('');
    try {
      const res = await fetch('/api/code-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: data.attendanceCode.trim() }),
      });

      const result = await res.json();

      if (!res.ok || !result.success) {
        let errorMessage = result.error || 'Login failed. Please check your code and try again.';
        if (res.status === 400) {
          errorMessage = 'Invalid code. Please enter a valid 4-digit number.';
        } else if (res.status === 404) {
          errorMessage = 'No student found with that code. Please double-check your code or ask your teacher.';
        } else if (res.status === 500) {
          errorMessage = result.error || 'Unable to sign in right now. Please try again in a moment.';
        }
        setCodeLoginError(errorMessage);
        toast.error(errorMessage);
        setIsLoading(false);
        return;
      }

      const supabase = createClient();
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });

      if (sessionError) {
        const msg = 'Session setup failed. Please try again.';
        setCodeLoginError(msg);
        toast.error(msg);
        setIsLoading(false);
        return;
      }

      localStorage.setItem('smartbls_student_session', JSON.stringify(result.student));
      toast.success('Welcome! Redirecting to your portal...');
      router.push('/student-learning-portal');
    } catch {
      const msg = 'Network error. Please check your connection and try again.';
      setCodeLoginError(msg);
      toast.error(msg);
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-border">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <Hash size={13} className="text-amber-600" />
        </div>
        <div>
          <p className="text-xs font-bold text-foreground">Student Code Login</p>
          <p className="text-xs text-muted-foreground">Pre / Post testing access</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onCodeSubmit)} className="space-y-3">
        {codeLoginError && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
            <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-600 font-medium">{codeLoginError}</p>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-foreground mb-1">
            4-Digit Attendance Code
          </label>
          <div className="relative">
            <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="e.g. 7342"
              className={`w-full pl-8 pr-4 py-2.5 rounded-lg border text-sm bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all text-center text-lg font-bold tracking-widest ${
                errors.attendanceCode ? 'border-red-400' : 'border-border'
              }`}
              {...register('attendanceCode', {
                required: 'Attendance code is required',
                pattern: { value: /^\d{4}$/, message: 'Enter a valid 4-digit code' },
                onChange: () => setCodeLoginError(''),
              })}
            />
          </div>
          {errors.attendanceCode && (
            <p className="text-red-500 text-xs mt-1">{errors.attendanceCode.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold text-xs flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 size={13} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Enter Portal with Code'
          )}
        </button>
      </form>
    </div>
  );
}

export default function AuthPageClient() {
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Auth Section */}
      <div className="flex flex-1 relative">
        {/* Left Brand Panel */}
        <AuthBrandPanel />

        {/* Right / Main Panel */}
        <div className="flex-1 flex flex-col min-h-screen lg:min-h-0 bg-card relative">

          {/* Admin Login — top-right corner button */}
          <div className="absolute top-4 right-4 z-20">
            {!showAdminLogin ? (
              <button
                onClick={() => setShowAdminLogin(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-background/80 backdrop-blur-sm text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40 transition-all shadow-sm"
              >
                <Lock size={13} />
                Admin Login
              </button>
            ) : (
              <button
                onClick={() => setShowAdminLogin(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-primary/30 bg-primary/5 text-xs font-semibold text-primary transition-all shadow-sm"
              >
                ✕ Close Admin Login
              </button>
            )}
          </div>

          {/* Admin Login Panel — slides in from top-right */}
          {showAdminLogin && (
            <div className="absolute top-14 right-4 z-30 w-full max-w-sm bg-card border border-border rounded-2xl shadow-2xl p-6 animate-fade-in overflow-y-auto max-h-[calc(100vh-5rem)]">
              <div className="mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Lock size={15} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">Admin / Host Sign In</p>
                  <p className="text-xs text-muted-foreground">Authorised accounts only</p>
                </div>
              </div>
              <LoginForm role="admin" key="admin-login" />
              <UniqueCodeLogin />
            </div>
          )}

          {/* Main Content — Interest Registration */}
          <div className="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 py-10 sm:py-14">
            <div className="w-full max-w-md">

              {/* Mobile Logo */}
              <div className="flex flex-col items-center gap-3 mb-6 lg:hidden">
                <div className="flex flex-col items-center gap-2 text-center">
                  <AppLogo size={40} />
                  <div>
                    <p className="font-extrabold text-lg sm:text-xl tracking-tight text-[rgba(194,10,29,1)] leading-tight">NATIONAL BLS DAY 2026</p>
                    <p className="font-bold text-sm sm:text-base text-[rgba(194,10,29,1)] leading-tight">BLS MARATHON</p>
                    <p className="text-xs sm:text-sm text-[rgba(194,10,29,1)] italic font-medium">"A City of Lifesavers"</p>
                  </div>
                </div>
                <div className="w-full bg-muted/50 border border-border rounded-xl px-3 sm:px-4 py-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3 text-center">Hosted By</p>
                  <OrganizerLogos size="sm" showLabels layout="grid" />
                </div>
              </div>

              {/* Interest Registration Form */}
              <div className="bg-background border border-border rounded-2xl shadow-card px-6 py-7">
                <InterestRegistrationForm />
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Event Photo Gallery Section */}
      <EventPhotoGallery />

      {/* Recent Events Section */}
      <section className="w-full bg-gradient-to-b from-background to-muted/30 border-t border-border py-12 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Section Heading */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 rounded-full bg-[rgba(194,10,29,1)]" />
            <h2 className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight">Recent Events</h2>
          </div>

          {/* Event Card */}
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            {/* Card Header */}
            <div className="bg-[rgba(194,10,29,1)] px-6 py-4">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-xs font-bold text-white/80 uppercase tracking-widest">National BLS Day</span>
                <span className="text-white/50 text-xs">•</span>
                <span className="text-xs font-semibold text-white/80">24/7 2026</span>
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold text-white leading-tight">
                BLS Marathon 2026 — <span className="italic font-semibold">"A City of Lifesavers"</span>
              </h3>
            </div>

            {/* Card Body */}
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm sm:text-base text-foreground leading-relaxed">
                The <strong>24/7 BLS Marathon 2026</strong> was a landmark community health initiative conducted on{' '}
                <strong>24th July 2026</strong>, bringing together students, healthcare professionals, and volunteers
                across the city for a full-day Basic Life Support (BLS) training programme.
              </p>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Organised under the banner of <strong>National BLS Day</strong>, the event aimed to equip participants
                with life-saving skills including CPR, AED usage, and emergency response techniques. Hundreds of
                participants were trained and certified, taking a significant step towards building a city of
                lifesavers.
              </p>

              {/* Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                {[
                  { label: 'Event Date', value: '24th July 2026' },
                  { label: 'Focus', value: 'BLS / CPR Training' },
                  { label: 'Theme', value: '"A City of Lifesavers"' },
                ].map((item) => (
                  <div key={item.label} className="bg-muted/50 rounded-xl px-4 py-3 text-center border border-border">
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1">{item.label}</p>
                    <p className="text-sm font-bold text-foreground">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Developer Credit */}
          <p className="mt-5 text-center text-xs text-muted-foreground">
            Platform developed by{' '}
            <span className="font-semibold text-foreground">Srilaya J, Student Developer</span>
            {' '}— powering seamless BLS training &amp; certification.
          </p>
        </div>
      </section>

      {/* Organising Team Section */}
      <OrganisingTeamSection />
    </div>
  );
}